import {
  Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ChangeDetectorRef,
  OnChanges, SimpleChange, SimpleChanges
} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {InfluencerService} from '../../../services/influencer/influencer.service';
import {DemographicHypeAuditor, HypeAuditorCountry, HypeAuditorGender, HAGenderDemographyByAge, HypeAuditorLanguage, HypeAuditorGenderType, HAAgeGroupItem, HypeAuditorCity} from '../../../../models/demographic/demographic-hype-auditor';
import {CommonConstant, listOrderedPlatforms, platformIconPath} from '../../../app.constants';
import {TranslateService} from '@ngx-translate/core';
import {PlaceholderVisibilityService} from '../../../services/placeholder-visibility/placeholder-visibility.service';
import { EventTrackerService, UserActionType, Page, ActivePages, TargetType } from '../../../services/event-tracker/event-tracker.service';
import {PdfExportService} from '../../../services/export-pdf/pdf-export.service';
import {UserService} from '../../../services/user.service';
import {CacheService} from '../../../services/cache/cache.service';
import {BlockedReason, InvalidAction, WallType} from '../../../../models/user';
import {forkJoin} from 'rxjs/observable/forkJoin';
import {Observable} from 'rxjs/Observable';
import {AchooPopupComponent} from '../../../core/achoo-popup/achoo-popup.component';
import {ModalService} from '../../../services/modal/modal.service';
import * as Sentry from '@sentry/browser';
import {StringUtils} from '../../../utils/string.utils';
import {Subscription} from 'rxjs/Subscription';
import { AppSettings } from '../../../utils/app-settings';
import {GenderPerAgeChartData} from '../../../../models/chart';
import { ReferenceDataService } from '../../../services/reference-data/reference-data.service';
import { ObjectUtils } from '../../../utils/object.utils';

enum CHART {
  defaultPieChartHight = 265,
}

export enum DemographicError {
  INVALID_LEVEL = 'invalid_level',
  DATA_UNAVAILABLE = 'data_unavailable',
  DATA_GENERATING = 'data_generating',
  NOT_FOUND_AUDIENCE_DATA = 'not_found_audience_data',
  UNSUPPORTED_PLATFORM = 'unsupported_platform'
}

@Component({
  selector: 'demographic-page',
  templateUrl: './demographic.component.html',
  styleUrls: ['./demographic.component.scss']
})
export class DemographicComponent implements OnInit, OnDestroy {
  private readonly dataOutdatedNotificationShowingTime = 99999;
  @ViewChild('DataGeneratingNotification') dataGeneratingNotification: AchooPopupComponent;
  @ViewChild('DataOutdatedNotification') dataOutdatedNotification: AchooPopupComponent;
  @ViewChild('UnSupportNetworkNotification') unSupportNetworkNotification: AchooPopupComponent;
  @ViewChild('UnsupportedPlatformNotification') unsupportedPlatformNotification: AchooPopupComponent;
  @Input() demoInfluencerId: number;
  @Input() pieCenter: number[];
  @Input() pieSize: number;
  @Input() pieInnerSize: number;
  @Input() pieLegend: any;
  @Input() pieHeight: number;
  @Input() pieTitleStyling: any;
  @Input() target: string;
  @Input() selectedChannel: number;
  @Input() printMode: boolean;

  // influencer mobile app
  @Input() isMobile = false;
  @Output() changeDemographicSourceEvent: EventEmitter<number> = new EventEmitter();
  @Output() emitSelectedDemographicSource = new EventEmitter();

  public selectedSource: number;
  public showDeepSocialDemographicData = false;
  public showHypeAuditorDemographicData = false;
  public showFallback = false;
  public showNotificationInFallback = false;

  public deepSocialData: any;
  public hypeAuditorData: DemographicHypeAuditor;

  public demographicSources = [];
  public canActivateSourceSelection = false;

  public errorCode: DemographicError;
  public fallbackMsgTitle: string;
  public fallbackMsgContent: string;
  public notificationMessage: string;

  private channelSubscription: Subscription;
  private creditAvailabilitySubscription: Subscription;
  private demographicSubscription: Subscription;
  private loaderTimeout;

  private readonly allPlatformOpt = {
    id: 0,
    name: this.translate.instant('channel_specific.choose_account'),
  };

  public isRedesignDemographic: boolean | false;

  public countryList: Array<object>;
  public languageList: Array<object>;

  constructor(private route: ActivatedRoute,
              private translate: TranslateService,
              private userService: UserService,
              private placeholderService: PlaceholderVisibilityService,
              private eventTracker: EventTrackerService,
              private influencerService: InfluencerService,
              private cacheService: CacheService,
              private pdfService: PdfExportService,
              private modalService: ModalService,
              private cdr: ChangeDetectorRef,
              private referenceDataService: ReferenceDataService) { }

  ngOnInit() {
    this.isRedesignDemographic = AppSettings.isRedesignDemographic();
    this.countryList = this.referenceDataService.countries;
    this.languageList = this.referenceDataService.languages;
    if (!this.demoInfluencerId) {
      this.route.parent.params.subscribe(res => {
        this.demoInfluencerId = res.id;
      });
    }
    if (this.selectedChannel) {
      this.selectedSource = this.selectedChannel;
    } else {
      this.route.queryParams.subscribe(params => {
        this.target = params.target;
        this.selectedSource = Number(params.demographicChannel);
      });
    }
    if (this.pieHeight === undefined) {
      this.pieHeight = CHART.defaultPieChartHight;
    }
    if (this.pieLegend === undefined) {
      this.pieLegend = {y: 31};
    }

    if (this.isMobile) {
      // watching active channel change
      this.channelSubscription = this.influencerService.activeChannel.subscribe((channel) => {
        if (channel && (!this.selectedSource || (channel.id !== this.selectedSource))) {
          const selectedChannel = this.demographicSources.find(s => s.id === channel.id);
          if (selectedChannel && selectedChannel.id > 0) {
            this.canActivateSourceSelection = false;
            this.onSelectDemographicSource({
              id: selectedChannel.id,
              platform: selectedChannel.platformName
            });
          } else {
            // hide everything when user select `all platform`, only show for specific channel
            this.selectedSource = this.allPlatformOpt.id;
            this.canActivateSourceSelection = true;
            this.showDeepSocialDemographicData = false;
            this.showHypeAuditorDemographicData = false;
            this.showFallback = false;
          }
        }
      });
      this.loadInfluencerChannels(this.demoInfluencerId);
    } else if (this.userService.isInfluencer) {
      this.loadInfluencerChannels(this.demoInfluencerId);
    } else {
      this.creditAvailabilitySubscription = this.influencerService.creditAvailabilityObserve.subscribe((res) => {
        if (res === true) {
          this.forceUpdateUI();
          this.loadInfluencerChannels(this.demoInfluencerId);
        }
        if (res === false) {
          this.forceUpdateUI();
          this.fallbackMsgTitle = this.translate.instant('INFLUENCER.insight.fallback_title');
          this.fallbackMsgContent = this.translate.instant('INFLUENCER.insight.fallback_msg', {
            maxCredit: this.userService.maxActiveInfluencers
          });
          this.showNotificationInFallback = true;
          this.showFallback = true;
          this.sendInvalidActionToSegement({
            wallName: 'Insights Limit',
            maxValue: this.userService.maxActiveInfluencers
          });
        }
      });
    }
  }

  private loadInfluencerChannels(influencerId) {
    this.cacheService.get(`influencer_${influencerId}`,
      this.influencerService.getInfluencer(influencerId)).subscribe((inf) => {
      if (!inf || !inf.channels) {
        return;
      }
      const inflChannels = inf.channels.filter(c => this.isSupportedPlatform(c.platformName.toLowerCase())).map(c => ({
        id: c.id,
        name: '@' + c.handle,
        platformName: c.platformName.toLowerCase(),
        platform: c.platformName.toLowerCase(),
        handle: c.handle
      }));
      if (this.isMobile) {
        inflChannels.unshift(this.allPlatformOpt);
      }
      this.demographicSources = inflChannels;
      this.canActivateSourceSelection = this.demographicSources.length > 0;
      let activeSource = this.demographicSources.find(s => s.id === this.selectedSource);
      // default on mobile is `All platform`
      // default on desktop is first Instagram channel
      if (!activeSource) {
        activeSource = this.isMobile ? this.allPlatformOpt : this.getDefaultSource(this.demographicSources);
      }
      if (activeSource) {
        this.onSelectDemographicSource(activeSource);
      } else if (!this.isMobile) {
        this.showFallback = true;
      }
      this.emitSelectedDemographicSource.emit(activeSource);
    });
  }

  ngOnDestroy() {
    this.modalService.close();
    if (this.channelSubscription) {
      this.channelSubscription.unsubscribe();
    }
    if (this.creditAvailabilitySubscription) {
      this.creditAvailabilitySubscription.unsubscribe();
    }
    if (this.demographicSubscription) {
      this.demographicSubscription.unsubscribe();
    }
  }

  onSelectDemographicSource(evt) {
    if (this.isSupportedPlatform(evt.platform)) {
      this.forceUpdateUI();
      this.selectedSource = evt.id;
      this.loadDemographicData(this.demoInfluencerId, evt.id, this.userService.getCompanyId(), false);
      this.changeDemographicSourceEvent.emit(evt.id);
      this.emitSelectedDemographicSource.emit(evt);
      this.influencerService.updateActiveChannel(evt);
    } else {
      try {
        const parameters = new URLSearchParams(window.location.href);
        // revert to previous channel if selected channel isn't supported
        if (parameters.get('channel')) {
          setTimeout(() => {
            this.selectedSource = Number(parameters.get('channel'));
          }, 20);
        }

        if (evt.id !== this.allPlatformOpt.id) {
          this.modalService.open(this.unSupportNetworkNotification, null, true);
        }
      } catch (error) {
        Sentry.captureException(new Error('Error occurred while attemping to get channel from url for demographic'));
      }
    }
  }

  private getDefaultSource(demographicSources) {
    if (demographicSources.length === 0) {
      return null;
    }
    const igSource = demographicSources.find(c => c.platform === 'instagram');
    return igSource ? igSource : demographicSources[0];
  }

  private isSupportedPlatform(platform) {
    return AppSettings.getActivePlatformList().indexOf(platform) >= 0;
  }

  private forceUpdateUI() {
    this.showDeepSocialDemographicData = false;
    this.showHypeAuditorDemographicData = false;
    this.showFallback = false;
    this.cdr.detectChanges();
  }

  private loadDemographicData(influencerId: number, influencerChannelId: number, companyId: number,
    needToLoadDSData?: boolean) {
    // don't fetch demographic data when user select `All platform` option
    if (influencerChannelId === this.allPlatformOpt.id) {
      return;
    }
    this.influencerService.checkViewDemographicPermission(companyId, influencerChannelId).subscribe((res) => {
      // fetch demographic data
      this.loadDemographicDataInternal(influencerId, influencerChannelId, companyId, needToLoadDSData);
      // segment track event view details group
      this.eventTracker.trackUserAction(UserActionType.VIEW, Page.DEMOGRAPHICS, {
        id: this.influencerService.influencerDetails.id,
        name: this.influencerService.influencerDetails.name,
        type: TargetType.INFLUENCER
      }, undefined, undefined, {
        type: ActivePages.INFLUENCER_DEMOGRAPHICS,
        name: this.influencerService.influencerDetails.name,
        id: this.influencerService.influencerDetails.id
      });
    }, (err) => {
      if (err.error && err.error.data === 'invalid_level') {
        this.userService.doInvalidActionByLevel('View Demographic', err.error.message);
        this.sendInvalidActionToSegement({
          wallName: 'Insights Limit',
          maxValue: this.userService.maxActiveInfluencers
        });
      }
      this.hideLoader();
      this.showFallback = true;
    });
  }

  private loadDemographicDataInternal(influencerId: number, influencerChannelId: number, companyId: number, needToLoadDSData?: boolean) {
    this.errorCode = null;
    const demographicRequests = [
      this.influencerService.loadInfluencEyeData(influencerChannelId, companyId).catch(error => {
        this.handleDemographicError(error);
        return Observable.of(null);
      })
    ];
    if (needToLoadDSData) {
      demographicRequests.push(this.influencerService.getInstagramAudienceData(influencerId).catch(error => {
        this.errorCode = DemographicError.NOT_FOUND_AUDIENCE_DATA;
        return Observable.of(null);
      }).map(ia => ia ? ia.audienceData : null));
    }
    this.executeDemographicRequests(demographicRequests, influencerChannelId, needToLoadDSData);
  }

  public executeDemographicRequests(demographicRequests: Observable<any>[], influencerChannelId, needToLoadDSData) {
    const demographicObserver = forkJoin(...demographicRequests);
    if (!this.cacheService.has(`demographic_data_${influencerChannelId}`)) {
      this.showLoader();
    }
    this.demographicSubscription = this.cacheService.get(`demographic_data_${influencerChannelId}`, demographicObserver)
      .subscribe((res) => {
        this.handleDemographicResponse(res, needToLoadDSData);
        this.userService.triggerUpdateActivatedInfluencers();
        // don't cache data if error
        if (this.errorCode) {
          this.cacheService.clear(`demographic_data_${influencerChannelId}`);
        }
      });
  }

  private handleDemographicResponse(res, needToLoadDSData) {
    this.hideLoader();
    // only get top 10 audienceInterest
    this.hypeAuditorData = res[0] ? Object.assign({}, res[0], {
      audienceInterest: res[0].audienceInterest && res[0].audienceInterest.length > 10 ? res[0].audienceInterest.slice(0, 10) : res[0].audienceInterest
    }) : null;
    if (needToLoadDSData) {
      this.deepSocialData = this.isRedesignDemographic ? this.convertDeepSocialDataToHypeAuditorData(res[1]) : res[1];
    }
    // if missing IE data but DS data is available, mark influencer as visited
    if (!this.hypeAuditorData && this.deepSocialData) {
      this.influencerService.visitInfluencer(this.demoInfluencerId, this.userService.getCompanyId()).subscribe();
    }
    this.showDemographicData();
  }

  private handleDemographicError(error) {
    if (!error || !error.error || !error.error.data) {
      this.fallbackMsgTitle = this.translate.instant('demographic_fallback.unexpected_error_title');
      this.fallbackMsgContent = this.translate.instant('demographic_fallback.unexpected_error_msg');
    } else if (error.error.data === 'invalid_level') {
      this.errorCode = DemographicError.INVALID_LEVEL;
      this.userService.doInvalidActionByLevel('Add to Campaign',
        this.translate.instant('common.invalid_action_by_levels_warning.influencer_profile.demographic_credit_exceed', {
          maxCredit: this.userService.maxActiveInfluencers
        }));
    } else if (error.error.data === 'data_unavailable') {
      this.errorCode = DemographicError.DATA_UNAVAILABLE;
      this.fallbackMsgTitle = this.translate.instant('demographic_fallback.audience_data_unavailable');
      this.fallbackMsgContent = error.error.message; // TODO: Never show messages from the WS, it can't be localised to user languages.
    } else if (error.error.data === 'data_generating') {
      this.errorCode = DemographicError.DATA_GENERATING;
      this.fallbackMsgTitle = this.translate.instant('demographic_fallback.audience_data_generating');
      this.fallbackMsgContent = this.translate.instant('demographic_fallback.audience_data_generating_msg');
    } else if (error.error.data === 'not_found_audience_data') {
      this.errorCode = DemographicError.NOT_FOUND_AUDIENCE_DATA;
      this.handleNotFoundInfluencerError();
    } else if (error.error.data === 'unsupported_platform') {
      this.errorCode = DemographicError.UNSUPPORTED_PLATFORM;
      this.fallbackMsgTitle = this.translate.instant('demographic_fallback.no_data_message_header');
      this.fallbackMsgContent = this.translate.instant('demographic_fallback.no_data_message_detail');
      this.modalService.close();
      this.modalService.open(this.unsupportedPlatformNotification, null, true);
    } else {
      this.fallbackMsgTitle = this.translate.instant('demographic_fallback.unexpected_error_title');
      this.fallbackMsgContent = this.translate.instant('demographic_fallback.unexpected_error_msg');
    }
  }

  private handleNotFoundInfluencerError() {
    this.fallbackMsgTitle = this.translate.instant('demographic_fallback.audience_data_not_found');
    const activeSource = this.demographicSources.find(s => s.id === this.selectedSource);
    this.fallbackMsgContent = this.translate.instant('demographic_fallback.audience_data_not_found_msg', {
      network: activeSource ? StringUtils.capitalize(activeSource.platformName) : ''
    });
  }

  private showDemographicData() {
    if (this.target === 'discover') {
      if (this.canShowDSData()) {
        this.showDeepSocial();
      } else if (this.hypeAuditorData) {
        this.showHAData();
      } else if (!this.pdfService.exportingPdf && this.errorCode === DemographicError.DATA_GENERATING) {
        // don't show fallback view on pdf report
        this.showFallback = true;
      }
    } else {
      if (this.hypeAuditorData) {
        this.showHAData();
      } else if (this.canShowDSData()) {
        this.showDeepSocial();
      } else {
        if (this.pdfService.exportingPdf) {
          return;
        }
        this.handleError();
      }
    }
  }

  private handleError() {
    if (this.errorCode === DemographicError.NOT_FOUND_AUDIENCE_DATA) {
      this.handleNotFoundInfluencerError();
    }

    this.showDeepSocialDemographicData = false;
    this.showFallback = true;
    this.cdr.detectChanges();
  }

  private canShowDSData() {
    const activeSource = this.demographicSources.find(s => s.id === this.selectedSource);
    return activeSource && activeSource.platformName === 'instagram' && this.deepSocialData;
  }

  private sendInvalidActionToSegement(limitInfo) {
    const detailAction = new InvalidAction(true, limitInfo.blockedReason, limitInfo.wallType, limitInfo.wallName, true, limitInfo.maxValue,
              limitInfo.desiredValue, this.userService.currentUserLevel.name);
    this.eventTracker.trackUserInvalidAction(detailAction, UserActionType.VIEW, Page.DEMOGRAPHICS, {
      id: this.influencerService.influencerDetails.id,
      name: this.influencerService.influencerDetails.name,
      type: TargetType.INFLUENCER
    });
  }

  private showDeepSocial() {
    this.showHypeAuditorDemographicData = false;
    const activeSource = this.demographicSources.find(s => s.id === this.selectedSource);
    if (activeSource && activeSource.platformName === 'instagram' && this.deepSocialData) {
      this.showDeepSocialDemographicData = true;
      this.showFallback = false;
      if ((this.hypeAuditorData || this.errorCode !== DemographicError.NOT_FOUND_AUDIENCE_DATA) && !this.pdfService.exportingPdf) {
        this.modalService.open(this.dataOutdatedNotification, null, true, this.dataOutdatedNotificationShowingTime);
      }
    } else {
      this.showDeepSocialDemographicData = false;
      this.showFallback = true;
    }

    this.cdr.detectChanges();
  }

  private showHAData() {
    this.showDeepSocialDemographicData = false;
    if (this.hypeAuditorData) {
      this.showHypeAuditorDemographicData = true;
      this.showFallback = false;
      if (this.hypeAuditorData.isOutdated && !this.pdfService.exportingPdf) {
        this.modalService.open(this.dataOutdatedNotification, null, true, this.dataOutdatedNotificationShowingTime);
      }
      if (this.hypeAuditorData.isNew) {
        this.influencerService.markPreloadedInfluencerOutdated(this.demoInfluencerId);
        this.hypeAuditorData.isNew = false;
      }
    } else {
      this.showHypeAuditorDemographicData = false;
      this.showFallback = true;
    }

    this.cdr.detectChanges();
  }

  private showFallbackView() {
    this.showHypeAuditorDemographicData = false;
    this.showDeepSocialDemographicData = false;
    this.showFallback = true;
  }

  openHypeAuditor() {
    this.target = null;
    this.modalService.close();
    if (this.hypeAuditorData && this.hypeAuditorData.isOutdated) {
      this.updateInfluencerDemographic(this.selectedSource, this.userService.getCompanyId());
    } else {
      this.showHAData();
    }
  }

  private updateInfluencerDemographic(channelId, companyId) {
    this.showLoader();
    // clear cache of old data
    this.cacheService.clear(`demographic_data_${channelId}`);
    this.influencerService.updateInfluencerDemographic(channelId, companyId).subscribe((res) => {
      this.hypeAuditorData = res;
      this.hideLoader();
      this.showHAData();
      this.userService.triggerUpdateActivatedInfluencers();
    }, (error) => {
      this.handleDemographicError(error);
      this.hideLoader();
      this.showFallbackView();
    });
  }

  closeNotification() {
    this.modalService.close();
  }

  get isExportingPdfReport() {
    return this.pdfService.exportingPdf;
  }
  get showDemographicSourceSelection() {
    return this.canActivateSourceSelection && !this.pdfService.exportingPdf;
  }

  private convertDeepSocialDataToHypeAuditorData(deepSocialData) {
    // no deep social data. Stop immediately
    if (!deepSocialData) {
      return;
    }

    const convertedHypeAuditorData: DemographicHypeAuditor = new DemographicHypeAuditor();
    let rawChartData: { content_persons_statistic: any; content_geo_statistic: any; audience_interests: any; audience_languages: any; };

    rawChartData = {
      content_persons_statistic: ObjectUtils.parseJSON(deepSocialData.content_persons_statistic),
      content_geo_statistic: ObjectUtils.parseJSON(deepSocialData.content_geo_statistic),
      audience_interests: ObjectUtils.parseJSON(deepSocialData.audience_interests),
      audience_languages: ObjectUtils.parseJSON(deepSocialData.audience_languages)
    };

    if (rawChartData.content_persons_statistic) {
      convertedHypeAuditorData.genders = this.convertAudienceGender(rawChartData.content_persons_statistic.genders);
      convertedHypeAuditorData.demographyByAge = this.convertGenderPerAgeFromDSToHA(rawChartData.content_persons_statistic.gendersPerAge, rawChartData.content_persons_statistic.genders);
    }

    if (rawChartData.content_geo_statistic) {
      convertedHypeAuditorData.countries = this.calculateTopItem(this.convertAudienceCountries(rawChartData.content_geo_statistic.countries));
      convertedHypeAuditorData.cities = this.convertAudienceCities(rawChartData.content_geo_statistic.cities);
    }

    if (rawChartData.audience_interests) {
      convertedHypeAuditorData.audienceInterest = this.calculateTopItem(this.convertAudienceInterest(rawChartData.audience_interests));
    }
    if (rawChartData.audience_languages) {
      convertedHypeAuditorData.languages = this.calculateTopItem(this.convertAudienceLanguages(rawChartData.audience_languages));
    }
    return convertedHypeAuditorData;
  }

  private convertGenderPerAgeFromDSToHA(genderPerAges: object, genders: object[]): HAGenderDemographyByAge[] {
    const femalePerAge = new HAGenderDemographyByAge();
    femalePerAge.gender = 'FEMALE';
    femalePerAge.value = Math.round(genders['FEMALE'] * 100);
    femalePerAge.by_age_group = Object.keys(genderPerAges).map((age) => {
      const ageGroup = new HAAgeGroupItem();
      ageGroup.group = age;
      ageGroup.value = Math.round(genderPerAges[age].FEMALE * 100);
      return ageGroup;
    });
    const malePerAge = new HAGenderDemographyByAge();
    malePerAge.gender = 'MALE';
    malePerAge.value = Math.round(genders['MALE'] * 100);
    malePerAge.by_age_group = Object.keys(genderPerAges).map((age) => {
      const ageGroup = new HAAgeGroupItem();
      ageGroup.group = age;
      ageGroup.value = Math.round(genderPerAges[age].MALE * 100);
      return ageGroup;
    });
    return [femalePerAge, malePerAge];
  }

  private convertAudienceGender(genders: object): HypeAuditorGender[] {
    const female = new HypeAuditorGender();
    female.gender = HypeAuditorGenderType.FEMALE;
    female.value = Math.round(genders['FEMALE'] * 100);
    const male = new HypeAuditorGender();
    male.gender = HypeAuditorGenderType.MALE;
    male.value = Math.round(genders['MALE'] * 100);
    return [male, female];
  }

  private convertAudienceInterest(audience_interests) {
    return Object.keys(audience_interests).map((key) => {
      const audienceInterest = [key, audience_interests[key]];
      return audienceInterest;
    });
  }

  private convertAudienceCountries(countries): HypeAuditorCountry[] {
    let result = Object.keys(countries).map((key) => ({name: key, value: Math.round(countries[key] * 100)}));
    result = this.convertCodeConstanst(result, this.countryList);
    return result;
  }

  private convertAudienceCities(cities): HypeAuditorCity[] {
    const mappedData = [];
    cities.forEach(item => {
      mappedData.push({name: item.name, value: Math.round(item.percentage * 100)});
    });
    return mappedData;
  }

  private convertAudienceLanguages(languages): HypeAuditorLanguage[] {
    const result = Object.keys(languages).map((key) => ({code: key, value:  Math.round(languages[key] * 100)}));
    return result;
  }

  /**
  * convert code to name (language, country,...) by using code
  * @param data, constants
  */
  private convertCodeConstanst(data: Array<any>, constants: Array<any>) {
    // create hash map constanst
    const constantsHash = constants.reduce((map, obj) => (map[obj.code] = obj.name, map), {});

    // Assign data point code to get name from contanst hash
    const transformedData = data.map(
      (item) => Object.assign(item, {
        name: constantsHash[item.name]
      })
    );

    return transformedData;
  }

    /**
   * calculate Top 10 Items for pie chart
   * @param data
   */
  private calculateTopItem(data) {
    // Get max 10 items
    const selectedItems = data.slice(0, CommonConstant.PIE_CHART_TOP_ITEM);

    // Get the sum of percentage for selected items
    const totalPercent = selectedItems.reduce(
      (accumulator, value) => accumulator + value.y,
      0
    );

    // Recalculate item percentages to be proportional to the sum of the top 10 items
    const calculatedItems = selectedItems.map(
      (item) => Object.assign(
        item,
        { y: (item.y * (100 / (totalPercent * 100)))}
      )
    );

    return calculatedItems;
  }

  private showLoader() {
    this.loaderTimeout = setTimeout(() => {
      this.showDeepSocialDemographicData = false;
      this.showHypeAuditorDemographicData = false;
      this.showFallback = false;
      this.placeholderService.show(CommonConstant.DEMOGRAPHIC_PLACEHOLDER);
    }, 500);
  }

  private hideLoader() {
    clearTimeout(this.loaderTimeout);
    this.placeholderService.hide(CommonConstant.DEMOGRAPHIC_PLACEHOLDER);
  }

}



// WEBPACK FOOTER //
// ./src/app/main/influencer/demographic/demographic.component.ts